import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChatStore } from '@application/chat.store';
import { Message } from '@domain/models';
import { DeliveryTarget } from '@domain/ports';
import { ToastService } from '../../../toast/toast.service';

@Component({
  selector: 'app-report-submission-modal',
  imports: [FormsModule],
  templateUrl: './report-submission-modal.component.html',
  styleUrl: './report-submission-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportSubmissionModalComponent implements OnInit {
  private readonly chatStore = inject(ChatStore);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly action = input.required<Message>();
  readonly closed = output<void>();
  readonly unavailable = output<string>();
  readonly refreshRequired = output<string>();

  readonly contextLoading = signal(false);
  readonly contextUnavailable = signal(false);
  readonly contextError = signal(false);
  readonly submitting = signal(false);
  readonly agentCode = signal('');
  readonly deliveryTarget = signal<DeliveryTarget>('REQUESTER');
  readonly canSubmit = computed(() => {
    const code = this.agentCode();
    return !this.submitting() && !this.contextLoading() && !this.contextError()
      && !this.contextUnavailable() && this.action().reportStatus === 'AVAILABLE'
      && !!code.trim() && code.length <= 120;
  });

  ngOnInit(): void {
    this.loadContext();
  }

  async loadContext(): Promise<void> {
    if (this.contextLoading()) return;
    this.contextLoading.set(true);
    this.contextError.set(false);
    this.contextUnavailable.set(false);
    try {
      const context = await this.chatStore.getReportContext(this.action().id);
      this.agentCode.set(context.agentCode);
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        this.closed.emit();
      } else if (error instanceof HttpErrorResponse && error.status === 404) {
        this.contextUnavailable.set(true);
        this.unavailable.emit(this.action().id);
      } else {
        this.contextError.set(true);
      }
    } finally {
      this.contextLoading.set(false);
    }
  }

  close(): void {
    if (!this.submitting()) this.closed.emit();
  }

  async submit(): Promise<void> {
    if (!this.canSubmit()) return;
    const code = this.agentCode();
    this.submitting.set(true);
    const id = this.action().id;
    const result = await this.chatStore.submitReport(id, code, this.deliveryTarget());
    if (this.destroyRef.destroyed) return;

    this.submitting.set(false);
    if (result.ok) {
      this.closed.emit();
      return;
    }

    const error = result.error;
    if (!(error instanceof HttpErrorResponse)) {
      this.toast.error('No pudimos completar la solicitud. Actualiza el chat para verificar su estado antes de volver a intentarlo.');
      this.refreshRequired.emit(id);
      this.closed.emit();
      return;
    }
    const errorCode = error.error?.code as string | undefined;
    if (error.status === 400) {
      this.toast.error('Revisa la placa y selecciona una opción de reporte.');
    } else if (error.status === 404) {
      this.unavailable.emit(id);
      this.closed.emit();
      this.toast.error('La solicitud de reporte ya no está disponible.');
    } else if (error.status === 409) {
      const messages: Record<string, string> = {
        WA0001: 'Esta solicitud de reporte está en proceso.',
        WA0002: 'Este reporte ya fue confirmado.',
        WA0003: 'No se pudo confirmar el envío anterior de este reporte.'
      };
      this.toast.error(messages[errorCode ?? ''] ?? 'La solicitud de reporte no está disponible.');
      if (errorCode === 'WA0001') this.refreshRequired.emit(id);
      else this.unavailable.emit(id);
      this.closed.emit();
    } else if (error.status !== 401 && error.status !== 403) {
      this.refreshRequired.emit(id);
      this.closed.emit();
      this.toast.error('No pudimos completar la solicitud. Actualiza el chat para verificar su estado antes de volver a intentarlo.');
    }
  }
}
